/**
 * Subscription Settings Page
 * Minimal settings — cancel/manage handled by Stripe Customer Portal
 */

import { openCustomerPortal } from "@/services/stripe";
import { useAuthStore } from "@/stores/authStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { Bell, ExternalLink, Settings, Shield } from "lucide-react";
import { useEffect, useState } from "react";

export default function SubscriptionSettingsPage() {
  const { token } = useAuthStore();
  const { currentSubscription, isLoading, fetchSubscription } =
    useSubscriptionStore();
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (token) fetchSubscription(token);
  }, [token, fetchSubscription]);

  const handleOpenPortal = async () => {
    if (!token) return;
    setPortalLoading(true);
    try {
      await openCustomerPortal(token);
    } catch {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Subscription Settings
        </h1>
        <p className="text-gray-500 mt-1">
          Manage your subscription preferences
        </p>
      </div>

      {/* Billing Management */}
      {currentSubscription?.hasStripeCustomer && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Billing & Subscription
            </h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Manage your payment methods, view invoices, change or cancel your
            subscription through the Stripe Customer Portal.
          </p>
          <button
            onClick={handleOpenPortal}
            disabled={portalLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {portalLoading ? "Opening..." : "Open Billing Portal"}
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Notification Preferences */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        </div>
        <div className="space-y-3">
          <label className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Payment receipts</span>
            <input type="checkbox" defaultChecked className="rounded" />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-sm text-gray-700">
              Subscription renewal reminders
            </span>
            <input type="checkbox" defaultChecked className="rounded" />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-sm text-gray-700">
              Plan expiration warnings
            </span>
            <input type="checkbox" defaultChecked className="rounded" />
          </label>
        </div>
      </div>

      {/* Data & Privacy */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Data & Privacy
          </h2>
        </div>
        <p className="text-sm text-gray-500">
          Your subscription data is processed securely via Stripe. HealthPass
          never stores your credit card details. All payment processing is
          PCI-DSS compliant.
        </p>
      </div>
    </div>
  );
}
